import React, { useRef, useState, useCallback } from "react";
import { useDAW } from "./DAWProvider";
import { useDAWSession } from "./DAWSessionProvider";

const AUDIO_ACCEPT = ".wav,.mp3,.ogg,.m4a,.aac,.flac,.webm";

export default function CollabPanel() {
  const session = useDAWSession();
  const { kit, canUploadRecord } = useDAW();
  const [requestSent, setRequestSent] = useState(false);
  const [selectedPad, setSelectedPad] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);

  if (!session) return null;

  const {
    sessionRole,
    collaborators,
    pendingRequests,
    maxCollaborators,
    requestAccess,
    cancelRequest,
    grantAccess,
    denyAccess,
    revokeAccess,
    sendCollabAction,
  } = session;

  const handleRequestAccess = () => {
    requestAccess("User", null);
    setRequestSent(true);
  };

  const handleCancelRequest = () => {
    cancelRequest();
    setRequestSent(false);
  };

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const arrayBuffer = await file.arrayBuffer();
    const name = file.name.replace(/\.[^.]+$/, "");
    sendCollabAction({
      type: "upload-pad",
      padIndex: selectedPad,
      audioData: arrayBuffer,
      name,
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [sendCollabAction, selectedPad]);

  const handleStartRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const arrayBuffer = await blob.arrayBuffer();
        sendCollabAction({
          type: "record-audio",
          audioData: arrayBuffer,
          name: `Recording ${Date.now()}`,
        });
        stream.getTracks().forEach((t) => t.stop());
        setIsRecording(false);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch (err) {
      console.error("Failed to start recording:", err);
    }
  }, [sendCollabAction]);

  const handleStopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  // --- Host view: manage requests and collaborators ---
  if (sessionRole === "host") {
    return (
      <div className="daw-collab-panel">
        <div className="daw-collab-panel__header">
          Collaborators ({collaborators.length}/{maxCollaborators})
        </div>

        {pendingRequests.length > 0 && (
          <div className="daw-collab-panel__section">
            <div className="daw-collab-panel__section-title">
              Requests ({pendingRequests.length})
            </div>
            {pendingRequests.map((req) => (
              <div key={req.userId} className="daw-collab-panel__user-row">
                <span className="daw-collab-panel__username">{req.username || "User"}</span>
                <div className="daw-collab-panel__actions">
                  <button
                    className="daw-collab-panel__btn daw-collab-panel__btn--accept"
                    onClick={() => grantAccess(req.userId, req.username, req.avatar)}
                    disabled={collaborators.length >= maxCollaborators}
                  >
                    Accept
                  </button>
                  <button
                    className="daw-collab-panel__btn daw-collab-panel__btn--deny"
                    onClick={() => denyAccess(req.userId)}
                  >
                    Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {collaborators.length > 0 && (
          <div className="daw-collab-panel__section">
            <div className="daw-collab-panel__section-title">Active</div>
            {collaborators.map((c) => (
              <div key={c.userId} className="daw-collab-panel__user-row">
                <span className="daw-collab-panel__username">{c.username || "User"}</span>
                <button
                  className="daw-collab-panel__btn daw-collab-panel__btn--revoke"
                  onClick={() => revokeAccess(c.userId)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {collaborators.length === 0 && pendingRequests.length === 0 && (
          <div className="daw-collab-panel__empty">
            Waiting for viewers to request access...
          </div>
        )}
      </div>
    );
  }

  // --- Collaborator view: upload and record ---
  if (sessionRole === "collaborator") {
    return (
      <div className="daw-collab-panel">
        <div className="daw-collab-panel__header daw-collab-panel__header--collab">
          Collaborator Access
        </div>

        <div className="daw-collab-panel__section">
          <div className="daw-collab-panel__section-title">Upload Sample to Pad</div>
          <select
            className="daw-collab-panel__pad-select"
            value={selectedPad}
            onChange={(e) => setSelectedPad(Number(e.target.value))}
          >
            {kit.pads.map((pad, i) => (
              <option key={i} value={i}>{pad.name}</option>
            ))}
          </select>
          <input
            ref={fileInputRef}
            type="file"
            accept={AUDIO_ACCEPT}
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
          <button
            className="daw-collab-panel__btn daw-collab-panel__btn--upload"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload Sample
          </button>
        </div>

        <div className="daw-collab-panel__section">
          <div className="daw-collab-panel__section-title">Record Audio</div>
          {isRecording ? (
            <button
              className="daw-collab-panel__btn daw-collab-panel__btn--recording"
              onClick={handleStopRecording}
            >
              Stop Recording
            </button>
          ) : (
            <button
              className="daw-collab-panel__btn daw-collab-panel__btn--record"
              onClick={handleStartRecording}
            >
              Start Recording
            </button>
          )}
        </div>
      </div>
    );
  }

  // --- Viewer view: request access ---
  return (
    <div className="daw-collab-panel">
      <div className="daw-collab-panel__header">Beat Lab</div>
      {requestSent ? (
        <div className="daw-collab-panel__section">
          <div className="daw-collab-panel__status">Request pending...</div>
          <button
            className="daw-collab-panel__btn daw-collab-panel__btn--cancel"
            onClick={handleCancelRequest}
          >
            Cancel Request
          </button>
        </div>
      ) : (
        <button
          className="daw-collab-panel__btn daw-collab-panel__btn--request"
          onClick={handleRequestAccess}
        >
          Request Access
        </button>
      )}
      {collaborators.length > 0 && (
        <div className="daw-collab-panel__section">
          <div className="daw-collab-panel__section-title">
            Collaborators ({collaborators.length}/{maxCollaborators})
          </div>
          {collaborators.map((c) => (
            <div key={c.userId} className="daw-collab-panel__user-row">
              <span className="daw-collab-panel__username">{c.username || "User"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
